/**
 * BillAudit.js  —  v34.97 (Feb 13 2026)
 *
 * Generic single-page cloud bill audit. One component, six clouds.
 * Reuses the existing backend endpoint POST /api/cloud-audit/csv with
 * the `provider` field. Adds a per-cloud download-instructions block,
 * accent color, and icon — but the upload/results flow is identical
 * across AWS, Azure, GCP, Snowflake, Databricks, DigitalOcean.
 *
 * Why one component: 6 clouds × ~150 lines each = 900 lines of
 * near-duplicate UI. The CLOUD_CONFIGS map below is the only thing
 * that changes per cloud. Maintenance stays sane.
 *
 * Usage in App.js:
 *   <Route path="/aws-bill-audit" element={<BillAudit cloud="aws" />} />
 *   <Route path="/azure-bill-audit" element={<BillAudit cloud="azure" />} />
 *   ...etc
 */
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Upload, FileText, Loader2, AlertTriangle, TrendingDown, CheckCircle2, ArrowRight,
  Cloud, Database, Snowflake, Activity, Server, Download,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import PublicNavbar from '../components/PublicNavbar';

const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Per-cloud configuration. ALL public-facing differences live here.
// ---------------------------------------------------------------------------
export const CLOUD_CONFIGS = {
  aws: {
    label: 'AWS',
    longLabel: 'AWS bill',
    accent: 'emerald',
    icon: Cloud,
    where: (
      <>AWS Console → <span className="text-slate-200">Billing & Cost Management</span> → <span className="text-slate-200">Bills</span> → <span className="text-emerald-300">Download CSV</span></>
    ),
    sample: 'EC2 · S3 · RDS · CloudFront · Lambda · ELB · DynamoDB',
    seoTitle: 'Audit your AWS bill in 60 seconds · CARTIEAI',
    seoDesc: 'Drop your AWS billing CSV. Get the top 5 cost leaks ranked by $/month plus quick-win actions. No signup. No credentials. Free forever.',
  },
  azure: {
    label: 'Azure',
    longLabel: 'Azure bill',
    accent: 'sky',
    icon: Cloud,
    where: (
      <>Azure Portal → <span className="text-slate-200">Cost Management + Billing</span> → <span className="text-slate-200">Cost Analysis</span> → <span className="text-sky-300">Export → CSV</span></>
    ),
    sample: 'Virtual Machines · Storage · SQL Database · App Service · Cosmos DB · Synapse',
    seoTitle: 'Audit your Azure bill in 60 seconds · CARTIEAI',
    seoDesc: 'Drop your Azure billing CSV. Get the top 5 cost leaks ranked by $/month plus quick-win actions. No signup. Free forever.',
  },
  gcp: {
    label: 'Google Cloud',
    longLabel: 'GCP bill',
    accent: 'amber',
    icon: Cloud,
    where: (
      <>Google Cloud Console → <span className="text-slate-200">Billing</span> → <span className="text-slate-200">Reports</span> → <span className="text-amber-300">Download CSV</span></>
    ),
    sample: 'Compute Engine · Cloud Storage · BigQuery · Cloud SQL · Vertex AI · GKE',
    seoTitle: 'Audit your Google Cloud bill in 60 seconds · CARTIEAI',
    seoDesc: 'Drop your GCP billing CSV. Get the top 5 cost leaks ranked by $/month plus quick-win actions. No signup. Free forever.',
  },
  snowflake: {
    label: 'Snowflake',
    longLabel: 'Snowflake bill',
    accent: 'cyan',
    icon: Snowflake,
    where: (
      <>Snowflake → <span className="text-slate-200">Admin</span> → <span className="text-slate-200">Usage</span> → <span className="text-cyan-300">Export to CSV</span></>
    ),
    sample: 'Warehouses · Storage · Data Transfer · Serverless · Snowpipe · Materialized Views',
    seoTitle: 'Audit your Snowflake bill in 60 seconds · CARTIEAI',
    seoDesc: 'Drop your Snowflake billing CSV. Find idle warehouses, over-provisioned compute, and runaway queries — ranked by $/month.',
  },
  databricks: {
    label: 'Databricks',
    longLabel: 'Databricks bill',
    accent: 'rose',
    icon: Activity,
    where: (
      <>Databricks → <span className="text-slate-200">Admin Settings</span> → <span className="text-slate-200">Account → Usage</span> → <span className="text-rose-300">Download CSV</span></>
    ),
    sample: 'Job Clusters · All-Purpose · SQL Warehouses · Photon · ML Runtime · Auto Loader',
    seoTitle: 'Audit your Databricks bill in 60 seconds · CARTIEAI',
    seoDesc: 'Drop your Databricks billing CSV. Find DBU waste, idle clusters, and the runaway notebooks burning your bill.',
  },
  digitalocean: {
    label: 'DigitalOcean',
    longLabel: 'DigitalOcean bill',
    accent: 'blue',
    icon: Server,
    where: (
      <>DigitalOcean → <span className="text-slate-200">Billing</span> → <span className="text-slate-200">Invoices</span> → <span className="text-blue-300">Download CSV</span></>
    ),
    sample: 'Droplets · Spaces · Volumes · Load Balancers · Databases · Kubernetes',
    seoTitle: 'Audit your DigitalOcean bill in 60 seconds · CARTIEAI',
    seoDesc: 'Drop your DigitalOcean billing CSV. Find unused droplets, idle volumes, and the leaks ranked by $/month.',
  },
};

// Tailwind colour utility maps keyed by accent — keeps JIT happy
const ACCENT = {
  emerald: { ring: 'ring-emerald-500/30', bg: 'bg-emerald-500/15', text: 'text-emerald-300', glow: 'shadow-emerald-500/20', border: 'border-emerald-500/30', solidBg: 'bg-emerald-500', solidBgHover: 'hover:bg-emerald-400', pillBg: 'bg-emerald-500/10', pillBorder: 'border-emerald-500/25', pillText: 'text-emerald-200', dragBg: 'bg-emerald-500/10', dragBorder: 'border-emerald-400', hoverBorder: 'hover:border-emerald-500/40' },
  sky:     { ring: 'ring-sky-500/30',     bg: 'bg-sky-500/15',     text: 'text-sky-300',     glow: 'shadow-sky-500/20',     border: 'border-sky-500/30',     solidBg: 'bg-sky-500',     solidBgHover: 'hover:bg-sky-400',     pillBg: 'bg-sky-500/10',     pillBorder: 'border-sky-500/25',     pillText: 'text-sky-200',     dragBg: 'bg-sky-500/10',     dragBorder: 'border-sky-400',     hoverBorder: 'hover:border-sky-500/40' },
  amber:   { ring: 'ring-amber-500/30',   bg: 'bg-amber-500/15',   text: 'text-amber-300',   glow: 'shadow-amber-500/20',   border: 'border-amber-500/30',   solidBg: 'bg-amber-500',   solidBgHover: 'hover:bg-amber-400',   pillBg: 'bg-amber-500/10',   pillBorder: 'border-amber-500/25',   pillText: 'text-amber-200',   dragBg: 'bg-amber-500/10',   dragBorder: 'border-amber-400',   hoverBorder: 'hover:border-amber-500/40' },
  cyan:    { ring: 'ring-cyan-500/30',    bg: 'bg-cyan-500/15',    text: 'text-cyan-300',    glow: 'shadow-cyan-500/20',    border: 'border-cyan-500/30',    solidBg: 'bg-cyan-500',    solidBgHover: 'hover:bg-cyan-400',    pillBg: 'bg-cyan-500/10',    pillBorder: 'border-cyan-500/25',    pillText: 'text-cyan-200',    dragBg: 'bg-cyan-500/10',    dragBorder: 'border-cyan-400',    hoverBorder: 'hover:border-cyan-500/40' },
  rose:    { ring: 'ring-rose-500/30',    bg: 'bg-rose-500/15',    text: 'text-rose-300',    glow: 'shadow-rose-500/20',    border: 'border-rose-500/30',    solidBg: 'bg-rose-500',    solidBgHover: 'hover:bg-rose-400',    pillBg: 'bg-rose-500/10',    pillBorder: 'border-rose-500/25',    pillText: 'text-rose-200',    dragBg: 'bg-rose-500/10',    dragBorder: 'border-rose-400',    hoverBorder: 'hover:border-rose-500/40' },
  blue:    { ring: 'ring-blue-500/30',    bg: 'bg-blue-500/15',    text: 'text-blue-300',    glow: 'shadow-blue-500/20',    border: 'border-blue-500/30',    solidBg: 'bg-blue-500',    solidBgHover: 'hover:bg-blue-400',    pillBg: 'bg-blue-500/10',    pillBorder: 'border-blue-500/25',    pillText: 'text-blue-200',    dragBg: 'bg-blue-500/10',    dragBorder: 'border-blue-400',    hoverBorder: 'hover:border-blue-500/40' },
};

const fmtUSD = (n) => {
  if (n === null || n === undefined) return '$0';
  if (n < 1) return `$${n.toFixed(2)}`;
  if (n < 1000) return `$${Math.round(n).toLocaleString()}`;
  if (n < 1_000_000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${(n / 1_000_000).toFixed(2)}M`;
};

// ---------------------------------------------------------------------------
// Upload zone
// ---------------------------------------------------------------------------
const UploadZone = ({ onFile, busy, error, cfg, a }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!/\.(csv|json)$/i.test(f.name)) {
      onFile(null, 'Only CSV or JSON files are supported.');
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      onFile(null, 'File too large (>25 MB). Trim your export to one month.');
      return;
    }
    onFile(f, null);
  };

  return (
    <div
      onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
      onClick={() => !busy && inputRef.current?.click()}
      className={`relative rounded-2xl border-2 border-dashed p-10 sm:p-14 text-center transition-all cursor-pointer ${
        busy ? `${a.border} ${a.dragBg} cursor-wait` :
        dragging ? `${a.dragBorder} ${a.dragBg}` :
        `border-white/15 bg-white/[0.02] ${a.hoverBorder} hover:bg-white/[0.04]`
      }`}
      data-testid="bill-audit-upload-zone"
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.json,text/csv,application/json"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="bill-audit-file-input"
      />
      <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${a.bg} ring-1 ${a.ring} shadow-lg ${a.glow} mb-4`}>
        {busy ? <Loader2 className={`w-6 h-6 ${a.text} animate-spin`} /> : <Upload className={`w-6 h-6 ${a.text}`} />}
      </div>
      <h3 className="text-lg font-semibold text-white mb-1">
        {busy ? 'Analyzing your bill…' : `Drop your ${cfg.label} billing CSV here`}
      </h3>
      <p className="text-sm text-slate-400 mb-4">
        {busy ? 'This usually takes 5-15 seconds.' : 'or click to browse. CSV & JSON supported. Max 25 MB.'}
      </p>
      {!busy && (
        <div className="text-[11px] text-slate-500 max-w-md mx-auto leading-relaxed">
          Where to get it: {cfg.where}. We process the file in-memory and never store it.
        </div>
      )}
      {error && (
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs" data-testid="bill-audit-error">
          <AlertTriangle className="w-3.5 h-3.5" />
          {error}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------
const Results = ({ data, onReset, cfg, a }) => {
  const top = data.top_services?.slice(0, 5) || [];
  const wins = data.quick_wins || [];

  return (
    <div className="space-y-6" data-testid="bill-audit-results">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Total monthly spend" value={fmtUSD(data.total_monthly_spend)} accent={cfg.accent} testid="bill-audit-total" />
        <Stat label="Estimated waste" value={fmtUSD(data.waste_found)} accent="rose" sub={`~${data.waste_percent}% of bill`} testid="bill-audit-waste" />
        <Stat label="Services scanned" value={String(data.services_found || data.resources_scanned)} accent="slate" testid="bill-audit-services" />
      </div>

      <section data-testid="bill-audit-top-leaks">
        <div className="flex items-center gap-2 mb-3">
          <TrendingDown className={`w-4 h-4 ${a.text}`} />
          <h2 className="text-lg font-semibold text-white">Top 5 leaks · ranked by $/month</h2>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5 overflow-hidden">
          {top.length === 0 && (
            <div className="px-4 py-8 text-center text-slate-400 text-sm">No services found. Check the CSV format.</div>
          )}
          {top.map((s, i) => (
            <div key={s.service + i} className="flex items-center gap-4 px-4 py-3 hover:bg-white/[0.03] transition-colors">
              <span className="text-xs font-mono text-slate-500 w-6 text-right">{i + 1}.</span>
              <span className="text-sm text-white flex-1 truncate" title={s.service}>{s.service}</span>
              <span className="text-sm font-mono text-slate-200 tabular-nums w-24 text-right">{fmtUSD(s.cost)}</span>
              <span className={`text-xs ${a.text} font-medium tabular-nums w-28 text-right hidden sm:inline`}>
                ~{fmtUSD(s.cost * 0.25)}/mo savings
              </span>
            </div>
          ))}
        </div>
      </section>

      {wins.length > 0 && (
        <section data-testid="bill-audit-quick-wins">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className={`w-4 h-4 ${a.text}`} />
            <h2 className="text-lg font-semibold text-white">Quick wins · do these this week</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {wins.map((w, i) => (
              <div key={i} className={`rounded-xl border ${a.border} ${a.pillBg.replace('/10','/5')} p-4`}>
                <p className="text-sm font-semibold text-white mb-2">{w.action}</p>
                <div className="flex items-center justify-between text-xs text-slate-300">
                  <span className={`font-mono ${a.text} tabular-nums`}>{fmtUSD(w.savings)}/mo</span>
                  <span className="text-slate-500">·</span>
                  <span>{w.effort}</span>
                  <span className="text-slate-500">·</span>
                  <span className={w.risk === 'None' ? a.text : 'text-amber-300'}>{w.risk} risk</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <button
          onClick={onReset}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-semibold border border-white/10 transition-colors"
          data-testid="bill-audit-run-again"
        >
          <Upload className="w-4 h-4" />
          Run another bill
        </button>
        <Link
          to="/cloud-bill-audit"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-semibold border border-white/10 transition-colors"
          data-testid="bill-audit-switch-cloud"
        >
          <Cloud className="w-4 h-4" />
          Try another cloud
        </Link>
        <Link
          to="/signup"
          className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl ${a.solidBg} ${a.solidBgHover} text-slate-950 font-semibold transition-colors`}
          data-testid="bill-audit-cta-signup"
        >
          Get the full report + auto-fix
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <p className="text-[11px] text-slate-500 text-center pt-2">
        Waste estimate uses the FinOps Foundation 2025 benchmark of ~28% average cloud waste. Your actual savings depend on architecture.
      </p>
    </div>
  );
};

const STAT_ACCENT = {
  emerald: 'text-emerald-300',
  sky: 'text-sky-300',
  amber: 'text-amber-300',
  cyan: 'text-cyan-300',
  rose: 'text-rose-300',
  blue: 'text-blue-300',
  slate: 'text-slate-200',
};

const Stat = ({ label, value, sub, accent = 'emerald', testid }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={testid}>
    <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1.5">{label}</div>
    <div className={`text-3xl font-bold tabular-nums ${STAT_ACCENT[accent] || STAT_ACCENT.emerald}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const Tile = ({ icon: Icon, title, body, a }) => (
  <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02]">
    <Icon className={`w-4 h-4 ${a.text} mb-2`} />
    <p className="text-sm font-semibold text-white mb-1">{title}</p>
    <p className="text-xs text-slate-400 leading-relaxed">{body}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Page shell — accepts a `cloud` prop
// ---------------------------------------------------------------------------
export default function BillAudit({ cloud = 'aws' }) {
  const cfg = CLOUD_CONFIGS[cloud] || CLOUD_CONFIGS.aws;
  const a = ACCENT[cfg.accent];
  const Icon = cfg.icon;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleFile = async (f, fileError) => {
    if (fileError) { setError(fileError); return; }
    if (!f) return;
    setError(null);
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', f);
      form.append('provider', cloud);
      const res = await axios.post(`${API}/api/cloud-audit/csv`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      setResult(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to analyze the file.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg).slice(0, 200));
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setResult(null); setError(null); };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEOHead title={cfg.seoTitle} description={cfg.seoDesc} path={`/${cloud}-bill-audit`} />
      <PublicNavbar />

      <main className="max-w-4xl mx-auto px-4 pt-24 pb-20">
        <header className="mb-8 text-center sm:text-left" data-testid="bill-audit-hero">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${a.pillBg} border ${a.pillBorder} mb-4`}>
            <Icon className={`w-3 h-3 ${a.text}`} />
            <span className={`text-[11px] ${a.pillText} font-medium tracking-wide`}>{cfg.label} Bill Audit · zero credentials</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-3">
            Audit your <span className={a.text}>{cfg.label}</span> bill in <span className={a.text}>60 seconds</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl">
            Drop your billing CSV. Get the top 5 cost leaks ranked by $/month, plus three quick-win actions you can do this week. Your file is processed in-memory and never stored.
          </p>
        </header>

        {!result && <UploadZone onFile={handleFile} busy={busy} error={error} cfg={cfg} a={a} />}
        {result && <Results data={result} onReset={reset} cfg={cfg} a={a} />}

        {!result && !busy && (
          <>
            <section className="mt-12" data-testid="bill-audit-what-we-look-for">
              <h2 className={`text-sm font-semibold uppercase tracking-[0.18em] ${a.text} mb-4`}>What we look for</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Tile icon={FileText}    title="Top spend by service"     body={`Find the services driving 80% of your bill — for ${cfg.label}, usually: ${cfg.sample.split(' · ').slice(0, 3).join(', ')}.`} a={a} />
                <Tile icon={TrendingDown} title="Industry waste benchmark" body="FinOps Foundation 2025 says ~28% of cloud spend is wasted. We show how your bill compares." a={a} />
                <Tile icon={CheckCircle2} title="3 quick-win actions"      body="Concrete next steps you can do this week — right-sizing, idle resources, reserved-instance gaps." a={a} />
              </div>
            </section>

            <section className="mt-10 p-5 rounded-2xl border border-white/10 bg-white/[0.02]" data-testid="bill-audit-format-help">
              <div className="flex items-start gap-3">
                <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${a.pillBg} ring-1 ${a.ring} flex-shrink-0`}>
                  <Download className={`w-4 h-4 ${a.text}`} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white mb-1">Don&apos;t have a {cfg.label} CSV handy?</p>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Sign in to your {cfg.label} console and follow the path above. Any cost-by-service export works — the parser auto-detects column names.
                  </p>
                  <p className="text-[11px] text-slate-500 mt-2">
                    <span className={a.text}>Services typically found:</span> {cfg.sample}
                  </p>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
